/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import enhanceSpeechThumbnailMobile1x from "./enhancespeech-thumbnail-mobile@1x.jpg";
import enhanceSpeechThumbnailMobile2x from "./enhancespeech-thumbnail-mobile@2x.jpg";
import enhanceSpeechThumbnailSmall1x from "./enhancespeech-thumbnail-small@1x.jpg";
import enhanceSpeechThumbnailSmall2x from "./enhancespeech-thumbnail-small@2x.jpg";
import enhanceSpeechThumbnailLarge1x from "./enhancespeech-thumbnail-large@1x.png";
import enhanceSpeechThumbnailLarge2x from "./enhancespeech-thumbnail-large@2x.png";

import micCheckThumbnailMobile1x from "./mic-check-thumbnail-mobile@1x.jpg";
import micCheckThumbnailMobile2x from "./mic-check-thumbnail-mobile@2x.jpg";
import micCheckThumbnailSmall1x from "./mic-check-thumbnail-small@1x.jpg";
import micCheckThumbnailSmall2x from "./mic-check-thumbnail-small@2x.jpg";
import micCheckThumbnailLarge1x from "./mic-check-thumbnail-large@1x.png";
import micCheckThumbnailLarge2x from "./mic-check-thumbnail-large@2x.png";

import studioThumbnailMobile1x from "./studio-thumbnail-mobile@1x.jpg";
import studioThumbnailMobile2x from "./studio-thumbnail-mobile@2x.jpg";
import studioThumbnailSmall1x from "./studio-thumbnail-small@1x.jpg";
import studioThumbnailSmall2x from "./studio-thumbnail-small@2x.jpg";

import betaBadgeIcon from "images/beta-badge.svg";

import { useMemo } from "react";
import * as React from "react";
import styled, { css } from "styled-components";
import { devices, useIsMobile } from "components/shared/Styles";
import { Link } from "react-router-dom";
import { webComponentShim } from "components/shared/WebComponentShim";
import { useScreenSize } from "components/shared/Styles";
import { AppPath } from "components/AppPath";
import { LocaleLink } from "utilities/NavigationUtilities";

export enum QuickToolType {
  EnhanceSpeech,
  MicCheck,
  Studio,
}

export enum QuickToolsMode {
  Light,
  Dark,
}

export enum QuickToolsSize {
  Small,
  Large,
}

const AILinkStyles = css<{ size: QuickToolsSize }>`
  cursor: pointer;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
  --spectrum-button-m-primary-fill-texticon-background-color: var(
    --spectrum-gray-800
  );

  ${devices.large} {
    max-width: ${({ size }): string =>
      size === QuickToolsSize.Small ? "265px" : "100%"};
  }

  ${devices.small} {
    flex-direction: row;
  }
`;

const AILink = styled(Link)<{ size: QuickToolsSize }>`
  ${AILinkStyles}
`;

const AIHTMLLink = styled(LocaleLink)<{ size: QuickToolsSize }>`
  ${AILinkStyles}
`;

export const AILinks = styled.div<{ mode: QuickToolsMode }>`
  // Fix for SWC bug: https://adobeprovideo.slack.com/archives/CESK60MQD/p1689197352956859
  font-family:
    adobe-clean,
    "Source Sans Pro",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Ubuntu,
    "Trebuchet MS",
    "Lucida Grande",
    sans-serif;
  -webkit-font-smoothing: antialiased;

  display: flex;
  flex-wrap: wrap;
  gap: 32px;

  ${AILink}, ${AIHTMLLink} {
    color: ${({ mode }): string => {
      switch (mode) {
        case QuickToolsMode.Dark:
          return "var(--spectrum-gray-800)";
        case QuickToolsMode.Light:
          return "var(--spectrum-global-color-static-white)";
      }
    }};
  }
`;

export const TextContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-direction: column;
  justify-content: center;
`;

export const AITitle = styled.div<{
  size: QuickToolsSize;
}>`
  font-weight: 800;
  display: flex;
  flex-direction: row;
  gap: 8px;

  line-height: ${({ size }): string =>
    size === QuickToolsSize.Large ? "22px" : "18px"};

  font-size: ${({ size }): string =>
    size === QuickToolsSize.Large ? "22px" : "18px"};

  ${devices.small} {
    margin-top: 0;
  }
`;

export const AIText = styled.div`
  font-size: 18px;
  line-height: 22px;
`;

export const AIImage = styled.img.attrs({ alt: "" })`
  border-radius: 16px;
  ${devices.small} {
    width: 72px;
    height: 72px;
  }
`;

interface ToolImageSrcSets {
  Mobile: string;
  Small: string;
  Large: string;
}

function getSrcSet(
  images: ToolImageSrcSets,
  size: QuickToolsSize,
  isMobile: boolean
): string {
  if (isMobile) {
    return images.Mobile;
  }
  return size === QuickToolsSize.Small ? images.Small : images.Large;
}

const enhanceSpeechImages: ToolImageSrcSets = {
  Mobile: `${enhanceSpeechThumbnailMobile1x} 1x, ${enhanceSpeechThumbnailMobile2x} 2x`,
  Small: `${enhanceSpeechThumbnailSmall1x} 1x, ${enhanceSpeechThumbnailSmall2x} 2x`,
  Large: `${enhanceSpeechThumbnailLarge1x} 1x, ${enhanceSpeechThumbnailLarge2x} 2x`,
};

const EnhanceSpeech: React.FC<{ size: QuickToolsSize }> = ({ size }) => {
  const isMobile = useIsMobile();
  return (
    <AILink
      title={I18n.t("links.enhanced_speech.title")}
      to="/enhance"
      size={size}
    >
      <AIImage srcSet={getSrcSet(enhanceSpeechImages, size, isMobile)} />
      <TextContainer>
        <AITitle size={size}>{I18n.t("links.enhanced_speech.title")}</AITitle>
        <AIText>{I18n.t("links.enhanced_speech.text")}</AIText>
        {size === QuickToolsSize.Large && (
          <sp-button
            treatment="fill"
            variant="primary"
            size="xl"
            style={{ marginTop: "16px", alignSelf: "start" }}
          >
            {I18n.t("links.try_for_free")}
          </sp-button>
        )}
      </TextContainer>
    </AILink>
  );
};

const micCheckImages: ToolImageSrcSets = {
  Mobile: `${micCheckThumbnailMobile1x} 1x, ${micCheckThumbnailMobile2x} 2x`,
  Small: `${micCheckThumbnailSmall1x} 1x, ${micCheckThumbnailSmall2x} 2x`,
  Large: `${micCheckThumbnailLarge1x} 1x, ${micCheckThumbnailLarge2x} 2x`,
};

const MicCheck: React.FC<{ size: QuickToolsSize }> = ({ size }) => {
  const isMobile = useIsMobile();
  return (
    <AILink title={I18n.t("links.miccheck.title")} to="/miccheck" size={size}>
      <AIImage srcSet={getSrcSet(micCheckImages, size, isMobile)} />
      <TextContainer>
        <AITitle size={size}>{I18n.t("links.miccheck.title")}</AITitle>
        <AIText>{I18n.t("links.miccheck.text")}</AIText>
        {size === QuickToolsSize.Large && (
          <sp-button
            treatment="fill"
            variant="primary"
            size="xl"
            style={{ marginTop: "16px", alignSelf: "start" }}
          >
            {I18n.t("links.try_for_free")}
          </sp-button>
        )}
      </TextContainer>
    </AILink>
  );
};

const studioImages: ToolImageSrcSets = {
  Mobile: `${studioThumbnailMobile1x} 1x, ${studioThumbnailMobile2x} 2x`,
  Small: `${studioThumbnailSmall1x} 1x, ${studioThumbnailSmall2x} 2x`,
  Large: `${studioThumbnailSmall1x} 1x, ${studioThumbnailSmall2x} 2x`,
};

export const BetaBadge = styled.img.attrs({
  src: betaBadgeIcon,
  alt: I18n.t("links.studio.beta"),
})``;

const NowAvailableBadgeButton = styled(webComponentShim("sp-button")).attrs({
  treatment: "fill",
  variant: "primary",
})`
  outline: 4px solid white;
  cursor: pointer;
  background: linear-gradient(133.13deg, #5266ff 5.68%, #b871ff 96.33%);
`;

const NowAvailableBadgeContainer = styled.div<{ size: QuickToolsSize }>`
  position: absolute;
  top: ${(props): string =>
    props.size === QuickToolsSize.Small ? "16px" : "-16px"};
  left: ${(props): string =>
    props.size === QuickToolsSize.Small ? "16px" : "-16px"};
`;

export const NowAvailableBadge: React.FC<{ size: QuickToolsSize }> = ({
  size,
}) => {
  const screenSize = useScreenSize();
  const containerSize = useMemo(() => {
    if (screenSize > 1000) {
      return QuickToolsSize.Large;
    }

    return QuickToolsSize.Small;
  }, [screenSize]);

  return (
    <NowAvailableBadgeContainer size={containerSize}>
      <NowAvailableBadgeButton size={"l"}>
        {I18n.t("landing.now_available")}
      </NowAvailableBadgeButton>
    </NowAvailableBadgeContainer>
  );
};

const ImageContainer = styled.div`
  position: relative;
`;

const Studio: React.FC = () => {
  const isMobile = useIsMobile();
  return (
    <AIHTMLLink
      size={QuickToolsSize.Small}
      to={AppPath.STUDIO}
      requireAuthentication={true}
    >
      <ImageContainer>
        <AIImage
          srcSet={getSrcSet(studioImages, QuickToolsSize.Small, isMobile)}
        />
      </ImageContainer>
      <TextContainer>
        <AITitle size={QuickToolsSize.Small}>
          {I18n.t("links.studio.title")}
          <BetaBadge />
        </AITitle>
        <AIText>{I18n.t("links.studio.text")}</AIText>
      </TextContainer>
    </AIHTMLLink>
  );
};

export interface QuickToolsProps {
  types: QuickToolType[];
  mode: QuickToolsMode;
  size?: QuickToolsSize;
}

export const QuickTools: React.FC<QuickToolsProps> = ({
  types,
  mode,
  size = QuickToolsSize.Small,
}) => {
  return (
    <sp-theme theme="express" scale="medium" color="light">
      <AILinks mode={mode}>
        {types.map((type) => {
          switch (type) {
            case QuickToolType.Studio:
              return <Studio key={QuickToolType.Studio} />;
            case QuickToolType.MicCheck:
              return <MicCheck size={size} key={QuickToolType.MicCheck} />;
            case QuickToolType.EnhanceSpeech:
              return (
                <EnhanceSpeech size={size} key={QuickToolType.EnhanceSpeech} />
              );
          }
        })}
      </AILinks>
    </sp-theme>
  );
};
